// The phone page's sheets (Settings, Guests): a native-style bottom sheet over a <dialog>.
// It slides up on a spring, stops at a detent ("medium": about half the screen, "large": nearly
// all of it, with the page behind pulled back), follows a drag on its grabber or header, and
// slides away on Done, Escape, a tap outside it, or a downward fling. A sheet always ends with
// dialog.close(), so "close" listeners (the unpair reset) still run, however it was dismissed.

export type Detent = "medium" | "large";

/** Where a drag ends: the detent nearest to where the sheet would coast to, or dismissed. */
export function settle(
  /** The sheet's top, px below its large position, when the finger lifts. */
  offset: number,
  /** Downward speed, px/ms (negative: upward). */
  velocity: number,
  /** Each detent's offset below large (large is 0). */
  detents: Partial<Record<Detent, number>>,
  /** The sheet's height at large: dismissal lies below it. */
  height: number,
): Detent | "dismiss" {
  const coast = offset + velocity * 180; // where a flick would carry it
  if (velocity > 1.1 && offset > (detents.medium ?? 0) - 40) return "dismiss";
  const stops: Array<[Detent | "dismiss", number]> = Object.entries(detents) as Array<[Detent, number]>;
  stops.push(["dismiss", height]);
  let best = stops[0];
  for (const s of stops) if (Math.abs(s[1] - coast) < Math.abs(best[1] - coast)) best = s;
  return best[0];
}

const SPRING =
  "linear(0, 0.0428, 0.1424, 0.2669, 0.3961, 0.5178, 0.6257, 0.7172, 0.7921, 0.8515, 0.8974, 0.9318, 0.957, 0.9749, 0.9872, 0.9952, 1.0003, 1.0031, 1.0046, 1.005, 1.0049, 1.0044, 1.0038, 1.0032, 1.0025, 1.002, 1.0015, 1.0011, 1)";
const IOS_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const supportsLinear = typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("transition-timing-function", "linear(0, 1)");
const EASE = supportsLinear ? SPRING : IOS_EASE;
const reduced = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

export interface SheetOptions {
  /** The detents it can rest at; it opens at the first. */
  detents: Detent[];
}

export class Sheet {
  private detent: Detent;
  private y = 0; // current translateY, px
  private anim: Animation | null = null;
  private closing = false;

  constructor(
    readonly dialog: HTMLDialogElement,
    private readonly opts: SheetOptions = { detents: ["large"] },
  ) {
    this.detent = opts.detents[0];
    // Escape: slide away rather than vanish.
    dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      void this.dismiss();
    });
    // A tap on the dimmed page around the sheet (the dialog's own box is the sheet).
    dialog.addEventListener("click", (e) => {
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (e.clientY < r.top || e.clientX < r.left || e.clientX > r.right) void this.dismiss();
    });
    dialog.addEventListener("close", () => this.cleanup());
    const grab = dialog.querySelector<HTMLElement>(".sheet-grab");
    if (grab) this.wireDrag(grab);
  }

  get open(): boolean {
    return this.dialog.open;
  }

  /** The sheet's travel below its large position for each detent. */
  private offsets(): Partial<Record<Detent, number>> {
    const h = this.dialog.offsetHeight || window.innerHeight;
    const out: Partial<Record<Detent, number>> = {};
    for (const d of this.opts.detents) out[d] = d === "large" ? 0 : Math.max(0, h - Math.round(window.innerHeight * 0.56));
    return out;
  }

  present(): void {
    if (this.dialog.open) return;
    this.closing = false;
    this.detent = this.opts.detents[0];
    this.dialog.showModal();
    this.dialog.classList.remove("closing");
    this.setDetent(this.detent);
    const h = this.dialog.offsetHeight || window.innerHeight;
    this.moveTo(this.offsets()[this.detent] ?? 0, h, reduced() ? 0 : 520);
  }

  async dismiss(): Promise<void> {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.add("closing");
    document.body.dataset.sheet = "";
    const h = this.dialog.offsetHeight || window.innerHeight;
    await this.moveTo(h + 24, this.y, reduced() ? 0 : 380, IOS_EASE);
    if (this.closing) this.dialog.close();
  }

  private cleanup(): void {
    this.closing = false;
    this.anim?.cancel();
    this.anim = null;
    this.dialog.classList.remove("closing", "dragging");
    this.dialog.style.transform = "";
    if (!document.querySelector("dialog.sheet[open]")) document.body.dataset.sheet = "";
  }

  private setDetent(d: Detent): void {
    this.detent = d;
    this.dialog.dataset.detent = d;
    document.body.dataset.sheet = d;
  }

  private moveTo(to: number, from = this.y, ms = 480, easing = EASE): Promise<void> {
    this.anim?.cancel();
    this.y = to;
    const end = `translateY(${to}px)`;
    this.dialog.style.transform = end;
    if (!ms || typeof this.dialog.animate !== "function") return Promise.resolve();
    const anim = this.dialog.animate([{ transform: `translateY(${from}px)` }, { transform: end }], { duration: ms, easing });
    this.anim = anim;
    return anim.finished.then(
      () => {
        if (this.anim === anim) this.anim = null;
      },
      () => {},
    );
  }

  private wireDrag(grab: HTMLElement): void {
    let start = 0;
    let base = 0;
    let lastY = 0;
    let lastT = 0;
    let vel = 0;
    let id: number | null = null;
    grab.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest("button")) return; // Done is a button, not a handle
      id = e.pointerId;
      grab.setPointerCapture(id);
      this.anim?.cancel();
      start = lastY = e.clientY;
      lastT = e.timeStamp;
      base = this.y;
      vel = 0;
      this.dialog.classList.add("dragging");
    });
    grab.addEventListener("pointermove", (e) => {
      if (e.pointerId !== id) return;
      let dy = base + e.clientY - start;
      if (dy < 0) dy = -Math.sqrt(-dy) * 2; // rubber band above large
      this.y = dy;
      this.dialog.style.transform = `translateY(${dy}px)`;
      const dt = Math.max(1, e.timeStamp - lastT);
      vel = 0.8 * ((e.clientY - lastY) / dt) + 0.2 * vel;
      lastY = e.clientY;
      lastT = e.timeStamp;
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      id = null;
      this.dialog.classList.remove("dragging");
      const h = this.dialog.offsetHeight || window.innerHeight;
      const to = settle(this.y, vel, this.offsets(), h);
      if (to === "dismiss") return void this.dismiss();
      this.setDetent(to);
      void this.moveTo(this.offsets()[to] ?? 0, this.y, reduced() ? 0 : 480);
    };
    grab.addEventListener("pointerup", end);
    grab.addEventListener("pointercancel", end);
  }
}
